/**
 * Hot & Cold Hold — Prep List Upload Modal.
 *
 * Mirror of `PrepListUploadModal.jsx` for the HOLDING flow. Two-step:
 *   Step 1: pick kitchen / hold date / hot-or-cold / upload file.
 *   Step 2: preview parsed rows, remap columns if auto-detect missed,
 *           trim bad rows, then commit → creates a `hold_prep_lists`
 *           doc with N pending items ready to be filled during service.
 *
 * Backend endpoint mirrors the cooking one, uses the same shared parser
 * so any customer sheet layout works as long as it has an Order # (opt),
 * Dish, and Start Time column (in any order, with any punctuation).
 */
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Upload, X, ChevronRight, ChevronLeft, Trash2, FileText, AlertTriangle, Flame, Snowflake } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

const MAX_ITEMS = 500;

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

export default function HoldPrepListUploadModal({ facilities, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [facilityId, setFacilityId] = useState("");
  const [holdDate, setHoldDate] = useState(todayISO());
  const [holdTime, setHoldTime] = useState("11:30");
  const [mode, setMode] = useState("hot");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ order_no: "", dish: "" });
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }, []);

  const doParse = async () => {
    if (!facilityId) { toast.error("Pick a kitchen first."); return; }
    if (!holdDate) { toast.error("Pick a hold date."); return; }
    if (!holdTime) { toast.error("Pick a start time."); return; }
    if (!file) { toast.error("Upload a .xlsx or .csv file."); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/hot-cold-hold/hold-prep-lists/parse", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping({
        order_no: data.detected?.order_no || "",
        dish: data.detected?.dish || "",
      });
      setRows((data.preview || []).map((r, idx) => ({ ...r, _key: `p${idx}` })));
      setStep(2);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't read the file.");
    } finally { setParsing(false); }
  };

  const applyMapping = (m) => {
    if (!preview || !m.dish) return;
    const remapped = (preview.rows || [])
      .map((r, idx) => ({
        _key: `m${idx}`,
        order_no: String(r[m.order_no] || "").trim(),
        dish: String(r[m.dish] || "").trim(),
      }))
      .filter((r) => r.dish);
    setRows(remapped);
  };

  const commit = async () => {
    if (!rows.length) { toast.error("No dishes to import."); return; }
    if (rows.length > MAX_ITEMS) { toast.error(`Max ${MAX_ITEMS} items per prep list.`); return; }
    const fac = facilities.find((f) => f.id === facilityId);
    if (!fac) { toast.error("Kitchen not found. Please re-select."); return; }
    setBusy(true);
    try {
      const fname = (file?.name || "").toLowerCase();
      const source = fname.endsWith(".csv") ? "csv"
        : fname.endsWith(".xlsx") ? "excel"
        : fname.endsWith(".pdf") ? "pdf"
        : "manual";
      const { data } = await api.post("/hot-cold-hold/hold-prep-lists", {
        site_id: fac.site_id,
        facility_id: facilityId,
        hold_date: holdDate,
        hold_time: holdTime,
        mode,
        source,
        file_name: file?.name || null,
        items: rows.map((r) => ({
          order_no: r.order_no || "",
          dish: r.dish,
        })),
      });
      toast.success(`Prep list created — ${data.prep_list.items.length} dishes ready to fill.`);
      onCreated(data.prep_list);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't create prep list.");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    api.get("/hot-cold-hold/hold-prep-lists/template.csv", { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "hold_prep_list_template.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't download template."));
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", zIndex: 12000 }}
      onClick={onClose} data-testid="hold-prep-list-upload-modal">
      <div className="vb-card p-5"
        style={{ maxWidth: 780, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
              Upload Hold Prep List
            </h2>
            <div className="text-[12px] opacity-70">
              Step {step} of 2 · {step === 1 ? "File & scope" : "Preview & commit"}
            </div>
          </div>
          <button onClick={onClose} className="vb-btn ghost sm" data-testid="hold-prep-upload-close">
            <X size={16} />
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] opacity-70">Kitchen</label>
              <select className="vb-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}
                data-testid="hold-prep-upload-facility">
                <option value="">Select a kitchen…</option>
                {facilities.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] opacity-70">Hold date</label>
                <input type="date" className="vb-input" value={holdDate}
                  min={todayISO()} max={maxDate}
                  onChange={(e) => setHoldDate(e.target.value)}
                  data-testid="hold-prep-upload-date" />
              </div>
              <div>
                <label className="text-[11px] opacity-70">Start time (applies to every dish)</label>
                <input type="time" className="vb-input" value={holdTime}
                  onChange={(e) => setHoldTime(e.target.value)}
                  data-testid="hold-prep-upload-time" />
              </div>
            </div>
            <div>
              <label className="text-[11px] opacity-70">Mode (locked for the whole list)</label>
              <div className="flex gap-2 mt-1">
                <button type="button"
                  className={`vb-btn sm ${mode === "hot" ? "" : "ghost"}`}
                  onClick={() => setMode("hot")}
                  data-testid="hold-prep-upload-mode-hot"
                  style={{ flex: 1, padding: "6px 10px" }}>
                  <Flame size={13} /> Hot hold
                </button>
                <button type="button"
                  className={`vb-btn sm ${mode === "cold" ? "" : "ghost"}`}
                  onClick={() => setMode("cold")}
                  data-testid="hold-prep-upload-mode-cold"
                  style={{ flex: 1, padding: "6px 10px" }}>
                  <Snowflake size={13} /> Cold hold
                </button>
              </div>
            </div>
            <div className="text-[11px] opacity-70">
              Every dish on the list shares the same start time (the station is loaded together).
              A prep list can't mix hot &amp; cold items — upload two lists if you need both.
              Hold date can be up to 7 days ahead.
            </div>

            <div className="p-4 rounded" style={{ border: "1px dashed var(--vb-border)", background: "var(--vb-bg-2,#faf6f1)" }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[13px] font-semibold">Prep sheet file</div>
                <button className="vb-btn ghost sm" onClick={downloadTemplate} type="button"
                  data-testid="hold-prep-upload-template">
                  <FileText size={14} /> Download template
                </button>
              </div>
              <input type="file" accept=".xlsx,.csv,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="hold-prep-upload-file" />
              <div className="text-[11px] opacity-60 mt-1">
                Accepts .xlsx, .csv, or .pdf. Legacy .xls files aren't supported.
                We only need Order # (optional) and Dish columns — start time comes from above.
                A mapping picker appears if the Dish column is missing.
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button className="vb-btn ghost" onClick={onClose} disabled={parsing}
                data-testid="hold-prep-upload-cancel">Cancel</button>
              <button className="vb-btn" onClick={doParse} disabled={parsing || !file}
                data-testid="hold-prep-upload-parse">
                {parsing ? "Reading…" : <>Next <ChevronRight size={14} /></>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            {!mapping.dish && (
              <div className="p-3 rounded" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)" }}>
                <div className="flex items-center gap-2 text-[12px]" style={{ color: "#991b1b" }}>
                  <AlertTriangle size={14} /> We couldn't auto-detect the Dish column. Pick it below.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              {["order_no", "dish"].map((k) => (
                <div key={k}>
                  <label className="text-[11px] opacity-70">
                    {k === "order_no" ? "Order # (optional)" : "Dish"} column
                  </label>
                  <select className="vb-input" value={mapping[k]}
                    onChange={(e) => { const m = { ...mapping, [k]: e.target.value }; setMapping(m); applyMapping(m); }}
                    data-testid={`hold-prep-upload-map-${k}`}>
                    <option value="">— pick column —</option>
                    {(preview.headers || []).map((h) => (<option key={h} value={h}>{h}</option>))}
                  </select>
                </div>
              ))}
            </div>

            <div className="text-[12px] opacity-70">
              {rows.length} row{rows.length === 1 ? "" : "s"} ready · start time <strong>{holdTime}</strong> · {holdDate}
              {preview.truncated && <> · file had more than {MAX_ITEMS} rows — truncated</>}.
            </div>

            <div className="vb-card overflow-auto" style={{ maxHeight: 340 }}>
              <table className="vb-table" data-testid="hold-prep-upload-preview">
                <thead><tr><th style={{ width: 90 }}>Order #</th><th>Dish</th><th style={{ width: 40 }}></th></tr></thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={3} style={{ textAlign: "center", padding: 20 }} className="opacity-60">
                      No rows mapped yet. Pick columns above.
                    </td></tr>
                  )}
                  {rows.map((r, idx) => (
                    <tr key={r._key} data-testid={`hold-prep-upload-row-${idx}`}>
                      <td>{r.order_no || <span className="opacity-40">—</span>}</td>
                      <td><strong>{r.dish}</strong></td>
                      <td>
                        <button className="vb-btn ghost sm" onClick={() => setRows((xs) => xs.filter((_, i) => i !== idx))}
                          data-testid={`hold-prep-upload-row-remove-${idx}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between mt-2">
              <button className="vb-btn ghost" onClick={() => setStep(1)} disabled={busy}
                data-testid="hold-prep-upload-back">
                <ChevronLeft size={14} /> Back
              </button>
              <button className="vb-btn" onClick={commit} disabled={busy || rows.length === 0}
                data-testid="hold-prep-upload-commit">
                <Upload size={14} /> {busy ? "Creating…" : `Create prep list (${rows.length})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
